import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { PlantBadge, plantAlt, Section, Heading, Spinner, EmptyState } from "../components/Bits";
import PageHero from "../components/PageHero";
import { Search } from "lucide-react";

const FILTERS = [
  { key: "all", label: "Alle" },
  { key: "edible", label: "Essbar" },
  { key: "medicinal", label: "Heilkräuter" },
  { key: "toxic", label: "Giftig" },
  { key: "tree", label: "Bäume" },
  { key: "shrub", label: "Sträucher" },
];

export default function Plants() {
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filter !== "all") params.set("category", filter);
    if (search) params.set("search", search);
    api
      .get(`/plants?${params.toString()}`)
      .then((r) => setPlants(r.data || []))
      .finally(() => setLoading(false));
  }, [filter, search]);

  return (
    <div data-testid="plants-page">
      <Section className="!pb-8">
        <PageHero slug="plants" variant="soft"
        defaultOverline="Pflanzendatenbank"
        defaultTitle="Wildpflanzen aus Luxemburg & der Großregion"
        defaultSubtitle="Filtere nach Kategorie oder suche direkt nach Name – jede Pflanze mit Foto, Saison und Sicherheitshinweisen."
      />
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8">
          <div className="relative w-full md:max-w-md">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 opacity-50" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="z. B. Brennnessel, Bärlauch …"
              className="w-full pl-11 pr-4 py-3 rounded-full border bg-white text-sm focus:outline-none focus:border-[var(--wp-primary)]"
              style={{ borderColor: "var(--wp-border)" }}
              data-testid="plants-search-input"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
                  filter === f.key
                    ? "bg-[var(--wp-primary)] text-[var(--wp-cream)] border-[var(--wp-primary)]"
                    : "bg-white text-[var(--wp-text)] hover:bg-[var(--wp-cream)]"
                }`}
                style={filter !== f.key ? { borderColor: "var(--wp-border)" } : {}}
                data-testid={`filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <Spinner />
        ) : plants.length === 0 ? (
          <EmptyState title="Keine Pflanzen gefunden" description="Versuche einen anderen Suchbegriff oder Filter." />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="plants-grid">
            {plants.map((p) => (
              <Link key={p.id} to={`/plants/${p.slug}`} className="plant-card group block" data-testid={`plant-card-${p.slug}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.image_url} alt={plantAlt(p)} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-display text-xl">{p.name_de}</h3>
                    <PlantBadge category={p.category} />
                  </div>
                  <div className="italic text-xs opacity-70 mb-2">{p.name_lat}</div>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--wp-text-muted)" }}>
                    {p.short_description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
